import OpenInNew from "@mui/icons-material/OpenInNew";
import { Alert, Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { StatusChip } from "components/StatusChip";
import { MouseEvent, useMemo } from "react";
import { Link } from "react-router-dom";

import {
  correctedToValue,
  correctionFieldLabel,
  correctionLineLabel,
  correctionStateLabel,
  correctionValueLabel,
  isCorrectionLanded,
  ManifestCorrectionRow,
} from "./manifestCorrectionDisplay";
import { PICKER_GRID_DENSITY, PICKER_GRID_HEIGHT } from "./pickerGrid";

type Props = {
  rows: ManifestCorrectionRow[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  /** True when a search is narrowing the list — drives the empty-state copy. */
  searching: boolean;
  selectedIds: string[];
  /** Picks the current search (or the row cap) is hiding — they will not be linked. */
  hiddenSelectedCount: number;
  onSelectionChange: (ids: string[]) => void;
  /** Builds the per-row manifest link-outs. */
  tenantId: string;
};

/**
 * Warns that picks the grid isn't currently showing won't be linked. Only the
 * visible selection is submitted, so without this the count staff see and the
 * count that gets recorded quietly disagree.
 */
function hiddenSelectedText(visibleCount: number, hiddenCount: number): string {
  const total = visibleCount + hiddenCount;
  const subject = hiddenCount === 1 ? "1 is hidden" : `${hiddenCount} are hidden`;
  const object = hiddenCount === 1 ? "it" : "them";
  return `${total} selected — ${subject} by the current search and won't be linked. Clear the search to include ${object}.`;
}

/**
 * Candidate-corrections grid for "mark resolved → manifest correction".
 * Multi-select: one exceedance month can take several manifest fixes, and each is
 * its own audit record.
 *
 * Completed and open corrections are listed together (the State column tells them apart).
 * If any selected one is open, saving parks the finding instead of resolving it.
 */
export function ManifestCorrectionPicker({
  rows,
  loading,
  search,
  onSearchChange,
  searching,
  selectedIds,
  hiddenSelectedCount,
  onSelectionChange,
  tenantId,
}: Props) {
  const columns = useMemo<CustomGridColDef<ManifestCorrectionRow>[]>(
    () => [
      { field: "manifestTrackingNumber", headerName: "Manifest", flex: 0.9, minWidth: 140 },
      {
        field: "wasteLineNumber",
        headerName: "Line",
        flex: 0.4,
        valueGetter: (_, row) => correctionLineLabel(row),
      },
      {
        field: "field",
        headerName: "Field",
        flex: 0.7,
        valueGetter: (_, row) => correctionFieldLabel(row.field),
      },
      {
        field: "eManifestValue",
        headerName: "Corrected to",
        flex: 0.7,
        valueGetter: (_, row) => correctionValueLabel(row.field, correctedToValue(row)),
      },
      {
        field: "status",
        headerName: "State",
        flex: 0.6,
        valueGetter: (_, row) => correctionStateLabel(row),
        renderCell: (params) => (
          <StatusChip
            color={isCorrectionLanded(params.row) ? "success" : "info"}
            label={correctionStateLabel(params.row)}
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 56,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="Open manifest in a new tab">
            <IconButton
              size="small"
              component={Link}
              to={`/o/${tenantId}/waste/manifests/${params.row.processedManifestId}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open manifest in a new tab"
              onClick={(e: MouseEvent) => e.stopPropagation()}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [tenantId]
  );

  return (
    <Box sx={{ mt: 1 }}>
      <TextField
        label="Search by manifest tracking number"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 1 }}
      />
      {hiddenSelectedCount > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {hiddenSelectedText(selectedIds.length, hiddenSelectedCount)}
        </Alert>
      )}
      <Box sx={{ height: PICKER_GRID_HEIGHT }}>
        <DataGrid<ManifestCorrectionRow>
          rows={rows}
          columns={columns}
          loading={loading}
          noRowsMessage={
            searching
              ? "No corrections match that tracking number."
              : "No manifest corrections for this facility in the affected period."
          }
          checkboxSelection
          disableRowSelectionExcludeModel
          rowSelectionModel={{ type: "include", ids: new Set(selectedIds) }}
          onRowSelectionModelChange={(model) => {
            onSelectionChange(model.type === "include" ? Array.from(model.ids).map(String) : []);
          }}
          {...PICKER_GRID_DENSITY}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
        Select every correction needed to address the difference between reported and calculated
        status.
      </Typography>
    </Box>
  );
}
